// src/dashboard/StockCritique.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Form, Dropdown } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

export default function StockCritique() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchStockCritique = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/stock_critique.php`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée. Veuillez vous reconnecter.");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            const result = await response.json();
            setData(result);
        } catch (err) {
            console.error("Erreur de chargement:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchStockCritique();
        let interval;
        if (autoRefresh) interval = setInterval(fetchStockCritique, 30000);
        return () => clearInterval(interval);
    }, [fetchStockCritique, autoRefresh]);

    const handleRefresh = () => fetchStockCritique();

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleCreateOrder = (produit) => {
        navigate(`/achats/commandesNouveau?produit=${produit.id_produit}&quantite=${produit.quantite_manquante}`);
    };

    const handleViewProduct = (produitId) => {
        navigate(`/stock/produits/${produitId}`);
    };

    const handleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);
        if (newSelectAll) setSelectedProducts(filteredProduits.map(p => p.id_produit));
        else setSelectedProducts([]);
    };

    const handleSelectProduct = (produitId) => {
        if (selectedProducts.includes(produitId)) {
            setSelectedProducts(selectedProducts.filter(id => id !== produitId));
        } else {
            setSelectedProducts([...selectedProducts, produitId]);
        }
    };

    const handleBulkOrder = () => {
        const produitsSelectionnes = filteredProduits.filter(p => selectedProducts.includes(p.id_produit));
        if (produitsSelectionnes.length > 0) {
            const produitsData = produitsSelectionnes.map(p => ({
                id: p.id_produit,
                quantite: p.quantite_manquante || 1,
                designation: p.designation,
                reference: p.reference
            }));
            navigate('/achats/commandesNouveau', {
                state: { produits: produitsData, type: 'bulk_order' }
            });
        }
    };

    /* ============================================================
       FILTRES
       ============================================================ */
    const filteredProduits = data?.produits?.filter(produit => {
        const matchesSearch = produit.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            produit.reference?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filter === 'all' || produit.niveau_alerte === filter;
        const matchesCategory = selectedCategory === 'all' || produit.nom_categorie === selectedCategory;
        return matchesSearch && matchesFilter && matchesCategory;
    }) || [];

    const categories = [...new Set(data?.produits?.map(p => p.nom_categorie) || [])].filter(Boolean);

    /* ============================================================
       HELPERS
       ============================================================ */
    const getNiveauConfig = (niveau) => {
        const configs = {
            'rupture':  { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', text: 'Rupture',  icon: 'ri-alert-line' },
            'critique': { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'Critique', icon: 'ri-alert-line' },
            'alerte':   { gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', text: 'Alerte',   icon: 'ri-information-line' }
        };
        return configs[niveau] || configs.alerte;
    };

    const getNiveauBadge = (niveau) => {
        const config = getNiveauConfig(niveau);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.text}
            </span>
        );
    };

    /* ============================================================
       GRAPHIQUES
       ============================================================ */
    const seriesCategories = {
        series: data?.categories?.map(c => c.nb_produits) || [],
        options: {
            chart: { type: 'donut', height: 320, toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif' },
            labels: data?.categories?.map(c => c.nom_categorie || 'Sans catégorie') || [],
            colors: [COLORS.danger, COLORS.warning, COLORS.info, COLORS.success, COLORS.purple || '#8b5cf6', '#ec4899'],
            legend: { position: 'bottom', fontSize: '11px', offsetY: 5 },
            tooltip: { y: { formatter: (value) => `${value} produit(s)` } },
            plotOptions: {
                pie: {
                    donut: {
                        size: '65%',
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                label: 'Total',
                                fontSize: '14px',
                                fontWeight: 600,
                                formatter: () => data?.produits?.length || 0
                            }
                        }
                    }
                }
            },
            dataLabels: { style: { fontSize: '11px', fontWeight: 500 } }
        }
    };

    const seriesHistorique = [{
        name: 'Alertes',
        data: data?.historique?.map(h => h.nb_alertes).reverse() || []
    }];

    const optionsHistorique = {
        chart: { type: 'area', height: 250, toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif' },
        colors: [COLORS.danger],
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.1 } },
        stroke: { curve: 'smooth', width: 2 },
        grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
        xaxis: {
            categories: data?.historique?.map(h => {
                const date = new Date(h.date_alerte);
                return `${date.getDate()}/${date.getMonth() + 1}`;
            }).reverse() || [],
            labels: { style: { fontSize: '11px', fontWeight: 500 } }
        },
        yaxis: {
            title: { text: 'Nombre d\'alertes', style: { fontSize: '11px', fontWeight: 500 } },
            labels: { style: { fontSize: '11px' } }
        },
        tooltip: { y: { formatter: (value) => `${value} alerte(s)` } }
    };

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading && !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-alert-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des stocks critiques
                        </h5>
                        <p className="text-muted small">Récupération des données en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={handleRefresh} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const selectedCount = selectedProducts.length;

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                                <i className="ri-alert-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Surveillance du stock critique</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                        <i className="ri-alert-line"></i> Ruptures {data?.statistiques?.nb_ruptures || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-alert-line"></i> Critiques {data?.statistiques?.nb_critiques || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-information-line"></i> Alertes {data?.statistiques?.nb_alertes || 0}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                            <div className="form-check form-switch d-flex align-items-center me-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                    id="autoRefresh"
                                />
                                <label className="form-check-label small ms-2" htmlFor="autoRefresh" style={{ color: '#64748b' }}>
                                    Auto
                                </label>
                            </div>
                            <Button className="btn-modern-outline" onClick={handleRefresh} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Dropdown align="end">
                                <Dropdown.Toggle className="btn-modern-success" disabled={selectedCount === 0}>
                                    <i className="ri-shopping-cart-line me-1"></i> Commander ({selectedCount})
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="dropdown-menu-charte">
                                    <Dropdown.Item onClick={handleBulkOrder}>
                                        <i className="ri-shopping-cart-line me-2" style={{ color: COLORS.success }}></i>
                                        Commander la sélection
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-danger">
                                <div className="kpi-icon-charte"><i className="ri-alert-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Rupture de stock</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.danger }}>
                                        {data?.statistiques?.nb_ruptures || 0}
                                    </h3>
                                    <span className="kpi-unit-charte">produits</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-alert-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Stock critique</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.warning }}>
                                        {data?.statistiques?.nb_critiques || 0}
                                    </h3>
                                    <span className="kpi-unit-charte">produits</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-information-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Stock en alerte</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.info }}>
                                        {data?.statistiques?.nb_alertes || 0}
                                    </h3>
                                    <span className="kpi-unit-charte">produits</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-money-euro-circle-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Valeur réappro.</span>
                                    <h3 className="kpi-value-charte" style={{ color: COLORS.primary }}>
                                        {formatCurrency(data?.statistiques?.valeur_reapprovisionnement || 0)}
                                    </h3>
                                    <span className="kpi-unit-charte">investissement</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== GRAPHIQUES ===== */}
                    <Row className="g-3 mb-4">
                        <Col xl="4" className="d-flex">
                            <Card className="border-0 shadow-sm rounded-3 w-100">
                                <Card.Header className="card-header-gradient-secondary">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-pie-chart-2-line text-white"></i>
                                        <h6 className="mb-0 fw-semibold text-white">Répartition par catégorie</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    {data?.categories?.length > 0 ? (
                                        <>
                                            <ReactApexChart series={seriesCategories.series} options={seriesCategories.options} type="donut" height={300} />
                                            <div className="mt-3">
                                                {data?.categories?.slice(0, 5).map((cat, idx) => (
                                                    <div key={idx} className="cat-row-charte">
                                                        <span className="cat-label-charte">
                                                            {cat.nom_categorie || 'Sans catégorie'}
                                                        </span>
                                                        <span className="cat-count-charte">{cat.nb_produits}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="empty-state-charte">
                                            <div className="empty-state-icon-charte" style={{ background: `linear-gradient(135deg, ${COLORS.secondary}15, ${COLORS.secondary}05)`, color: COLORS.secondary }}>
                                                <i className="ri-pie-chart-2-line"></i>
                                            </div>
                                            <h6 className="fw-semibold mb-1">Aucune catégorie</h6>
                                            <p className="text-muted small mb-0">Aucune catégorie concernée</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xl="8" className="d-flex">
                            <Card className="border-0 shadow-sm rounded-3 w-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-bar-chart-line text-white"></i>
                                        <h6 className="mb-0 fw-semibold text-white">Historique des alertes (30 jours)</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <ReactApexChart series={seriesHistorique} options={optionsHistorique} type="area" height={250} />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="4">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher un produit..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={filter}
                                        onChange={(e) => setFilter(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="all">Tous les niveaux</option>
                                        <option value="rupture">🔴 Rupture</option>
                                        <option value="critique">🟠 Critique</option>
                                        <option value="alerte">🔵 Alerte</option>
                                    </Form.Select>
                                </Col>
                                <Col md="3">
                                    <Form.Select
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        className="filter-select-charte"
                                    >
                                        <option value="all">Toutes catégories</option>
                                        {categories.map((cat) => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md="2" className="text-end">
                                    <span className="count-badge-charte">
                                        <i className="ri-stack-line"></i>
                                        {filteredProduits.length} produit(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4" style={{ width: '40px' }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectAll && filteredProduits.length > 0}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                            <th>Référence</th>
                                            <th>Produit</th>
                                            <th className="text-end">Stock actuel</th>
                                            <th className="text-end">Seuil</th>
                                            <th className="text-end">Manquant</th>
                                            <th>Emplacement</th>
                                            <th className="text-center">Niveau</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredProduits.length > 0 ? (
                                            filteredProduits.map((produit) => {
                                                const manquant = produit.seuil_alerte - produit.quantite_reelle;
                                                const pourcentage = Math.min((produit.quantite_reelle / produit.seuil_alerte) * 100, 100);
                                                return (
                                                    <tr key={produit.id_produit} className={produit.niveau_alerte === 'rupture' ? 'row-late-charte' : ''}>
                                                        <td className="ps-4">
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={selectedProducts.includes(produit.id_produit)}
                                                                onChange={() => handleSelectProduct(produit.id_produit)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <span className="reference-badge-charte">
                                                                <i className="ri-barcode-line"></i>{produit.reference}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold" style={{ color: COLORS.primaryDark, fontSize: '0.85rem' }}>
                                                                {produit.designation}
                                                            </div>
                                                            {produit.code_barre && (
                                                                <small className="text-muted font-monospace" style={{ fontSize: '0.7rem' }}>
                                                                    {produit.code_barre}
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td className="text-end">
                                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                                <span className={`fw-bold ${produit.quantite_reelle <= 0 ? 'text-danger' : ''}`}>
                                                                    {formatNumber(produit.quantite_reelle)}
                                                                </span>
                                                                <div className="progress-charte" style={{ width: '70px' }}>
                                                                    <div
                                                                        className="progress-bar-charte"
                                                                        style={{
                                                                            width: `${pourcentage}%`,
                                                                            background: pourcentage <= 0
                                                                                ? `linear-gradient(90deg, ${COLORS.danger}, #dc2626)`
                                                                                : pourcentage <= 30
                                                                                ? `linear-gradient(90deg, ${COLORS.warning}, #d97706)`
                                                                                : `linear-gradient(90deg, ${COLORS.info}, #2563eb)`
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-end text-muted">{formatNumber(produit.seuil_alerte)}</td>
                                                        <td className="text-end">
                                                            {manquant > 0 ? (
                                                                <span className="text-danger fw-bold">{formatNumber(manquant)}</span>
                                                            ) : '—'}
                                                        </td>
                                                        <td className="text-muted">
                                                            {produit.emplacement ? (
                                                                <span>
                                                                    <i className="ri-map-pin-line me-1" style={{ fontSize: '0.75rem' }}></i>
                                                                    {produit.emplacement}
                                                                </span>
                                                            ) : '—'}
                                                        </td>
                                                        <td className="text-center">{getNiveauBadge(produit.niveau_alerte)}</td>
                                                        <td className="text-center pe-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <button
                                                                    className="action-btn-charte"
                                                                    onClick={() => handleViewProduct(produit.id_produit)}
                                                                    title="Voir le produit"
                                                                    style={{ '--btn-color': COLORS.info }}
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </button>
                                                                <button
                                                                    className="action-btn-charte"
                                                                    onClick={() => handleCreateOrder(produit)}
                                                                    title="Créer une commande"
                                                                    style={{ '--btn-color': COLORS.success }}
                                                                >
                                                                    <i className="ri-shopping-cart-line"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="9">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}15, ${COLORS.success}05)`, color: COLORS.success }}>
                                                            <i className="ri-checkbox-circle-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucun produit en stock critique</h6>
                                                        <p className="text-muted small mb-0">Tous les stocks sont à des niveaux acceptables</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== SUGGESTIONS ===== */}
                    {data?.suggestions?.length > 0 && (
                        <Card className="border-0 shadow-sm rounded-3 mt-4">
                            <Card.Header className="card-header-gradient-secondary">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="section-icon-dark-charte">
                                        <i className="ri-lightbulb-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold text-white">Suggestions de réapprovisionnement</h6>
                                    <span className="badge-header-light ms-auto">
                                        {data.suggestions.length} suggestion{data.suggestions.length > 1 ? 's' : ''}
                                    </span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row className="g-3">
                                    {data.suggestions.slice(0, 3).map((suggestion, idx) => (
                                        <Col md="4" key={idx}>
                                            <div className="suggestion-card-charte">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <span className="reference-badge-charte">
                                                        <i className="ri-barcode-line"></i>{suggestion.reference}
                                                    </span>
                                                    <div className="suggestion-icon-charte">
                                                        <i className="ri-shopping-cart-line"></i>
                                                    </div>
                                                </div>
                                                <h6 className="fw-semibold mb-1" style={{ color: COLORS.primaryDark, fontSize: '0.9rem' }}>
                                                    {suggestion.designation}
                                                </h6>
                                                <small className="text-muted d-block mb-3" style={{ fontSize: '0.75rem' }}>
                                                    <i className="ri-store-line me-1"></i>
                                                    {suggestion.fournisseur || 'Fournisseur non spécifié'}
                                                </small>
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <span className="text-muted small" style={{ fontSize: '0.7rem' }}>À commander</span>
                                                    <span className="fw-bold" style={{ color: COLORS.danger, fontSize: '0.9rem' }}>
                                                        {formatNumber(suggestion.quantite_a_commander)}
                                                    </span>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <span className="text-muted small" style={{ fontSize: '0.7rem' }}>Coût estimé</span>
                                                    <span className="fw-bold" style={{ color: COLORS.primary, fontSize: '0.9rem' }}>
                                                        {formatCurrency(suggestion.cout_total)}
                                                    </span>
                                                </div>
                                                <Button
                                                    onClick={() => handleCreateOrder(suggestion)}
                                                    className="btn-modern-outline w-100 justify-content-center"
                                                    size="sm"
                                                >
                                                    <i className="ri-shopping-cart-line me-1"></i> Commander
                                                </Button>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                                {data.suggestions.length > 3 && (
                                    <div className="text-center mt-4">
                                        <Link to="/achats/commandes/suggestions" className="btn-link-charte">
                                            Voir toutes les suggestions ({data.suggestions.length})
                                            <i className="ri-arrow-right-line ms-1"></i>
                                        </Link>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--danger);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== HEADERS GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .card-header-gradient-secondary {
                    background: linear-gradient(135deg, var(--secondary) 0%, #0d8ba6 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                .section-icon-dark-charte {
                    width: 32px; height: 32px; border-radius: 10px;
                    background: rgba(255,255,255,0.15);
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-size: 1rem;
                }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tbody tr.row-late-charte { background: #fef2f2; }
                .table-charte tbody tr.row-late-charte:hover { background: #fee2e2; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== PROGRESS ===== */
                .progress-charte {
                    height: 6px; border-radius: 4px;
                    background: #f1f5f9;
                    overflow: hidden;
                }
                .progress-bar-charte {
                    height: 100%; border-radius: 4px;
                    transition: width 0.6s ease;
                }

                /* ===== CAT ROW ===== */
                .cat-row-charte {
                    display: flex; justify-content: space-between;
                    align-items: center;
                    padding: 0.6rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                }
                .cat-row-charte:last-child { border-bottom: none; }
                .cat-label-charte {
                    font-size: 0.8rem; font-weight: 600;
                    color: var(--primary-dark);
                }
                .cat-count-charte {
                    background: #f1f5f9; color: #64748b;
                    border-radius: 50px; padding: 0.15rem 0.6rem;
                    font-size: 0.7rem; font-weight: 700;
                }

                /* ===== SUGGESTION CARD ===== */
                .suggestion-card-charte {
                    background: #f8fafc;
                    padding: 1.25rem;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    transition: all 0.3s ease;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }
                .suggestion-card-charte:hover {
                    background: white;
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                    transform: translateY(-3px);
                    border-color: var(--secondary);
                }
                .suggestion-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    background: ${COLORS.primary}15;
                    color: var(--primary);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.1rem;
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem;
                }

                /* ===== DROPDOWN ===== */
                .dropdown-menu-charte {
                    border: none; border-radius: 12px;
                    box-shadow: 0 12px 32px rgba(0,0,0,0.12);
                    padding: 0.5rem;
                }
                .dropdown-menu-charte .dropdown-item {
                    border-radius: 8px;
                    padding: 0.5rem 0.75rem;
                    font-size: 0.85rem;
                    transition: all 0.2s ease;
                }
                .dropdown-menu-charte .dropdown-item:hover { background: #f1f5f9; }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-success:disabled { opacity: 0.6; transform: none; }
                .btn-link-charte {
                    color: var(--primary); text-decoration: none;
                    font-weight: 600; font-size: 0.85rem;
                    transition: color 0.2s;
                    display: inline-flex; align-items: center;
                }
                .btn-link-charte:hover { color: var(--primary-dark); text-decoration: underline; }

                /* ===== ALERTE ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--danger);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--danger);
                }

                /* ===== FORM CHECK ===== */
                .form-check-input:checked {
                    background-color: var(--primary);
                    border-color: var(--primary);
                }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}